/**
 * Header do Flabom (Figma 03 · Header, 03b · Home transparente, 60 · Megamenu, 61 · Drawer).
 *
 * Três responsabilidades:
 *  1. <fb-menu-drawer>  — drawer mobile de tela cheia (doc 61). <dialog> nativo em modo
 *     modal: o próprio browser prende o foco, devolve o foco ao gatilho ao fechar e
 *     trata o Esc. Substitui o antigo <fb-mobile-drawer> (menu plano sem submenus).
 *  2. Megamenu desktop  — abre no hover/foco do item de 1º nível (doc 60) e coloca o
 *     header no estado "aberto" de 88px.
 *  3. Header transparente da Home (doc 03b) — o Figma só desenha o estado no topo da
 *     página. Decisão documentada: fica transparente enquanto o hero ainda passa por
 *     baixo do header e vira branco sólido (a mesma variante da PLP) depois disso.
 */

class FlabomMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openButtons = document.querySelectorAll(`[data-fb-drawer-open="${this.id}"]`);
    this.closeButtons = this.querySelectorAll('[data-fb-drawer-close]');

    this.openButtons.forEach((button) => button.addEventListener('click', () => this.open()));
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));

    this.dialog?.addEventListener('click', (event) => {
      // O backdrop do <dialog> reporta o próprio dialog como target do clique.
      if (event.target === this.dialog) this.close();
    });

    this.dialog?.addEventListener('close', () => this.syncExpanded(false));

    // Abaixo de 1366px o desktop também usa o drawer (nav não cabe — auditoria A1).
    this.mediaQuery = window.matchMedia('(min-width: 1366px)');
    this.mediaQuery.addEventListener('change', (event) => {
      if (event.matches) this.close();
    });
  }

  syncExpanded(state) {
    this.openButtons.forEach((button) => button.setAttribute('aria-expanded', String(state)));
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    this.syncExpanded(true);
  }

  close() {
    if (!this.dialog?.open) return;
    this.dialog.close();
  }
}

if (!customElements.get('fb-menu-drawer')) {
  customElements.define('fb-menu-drawer', FlabomMenuDrawer);
}

function flabomHeaderChrome() {
  const DESKTOP = '(min-width: 1024px)';
  const header = document.querySelector('header-component#header-component.fb-header');
  if (!header) return;

  const anchor = header.closest('.fb-header__anchor');
  const desktop = window.matchMedia(DESKTOP);
  const isTransparent = header.dataset.fbTransparent === 'true';

  const triggers = Array.from(header.querySelectorAll('[data-fb-megamenu-trigger]'));
  const panels = Array.from(header.querySelectorAll('[data-fb-megamenu-panel]'));

  let openPanelId = null;
  let anchorTop = 0;
  let solidFrom = Number.POSITIVE_INFINITY;
  let ticking = false;

  /* ------------------------------------- header transparente da Home (03b) */

  function measure() {
    if (!isTransparent || !anchor) return;
    anchorTop = anchor.getBoundingClientRect().top + window.scrollY;

    // O "hero" é a primeira seção do conteúdo da página (arquivo da Frente G).
    // Enquanto ele estiver passando por baixo do header, o header segue transparente.
    const hero = document.querySelector('#MainContent .shopify-section');
    if (hero) {
      const rect = hero.getBoundingClientRect();
      solidFrom = rect.top + window.scrollY + rect.height - header.offsetHeight;
    } else {
      solidFrom = anchorTop;
    }
  }

  function updateSkin() {
    if (!isTransparent || !anchor) return;

    if (!desktop.matches) {
      header.classList.remove('is-fixed', 'fb-header--light');
      return;
    }

    const y = window.scrollY;
    header.classList.toggle('is-fixed', y > anchorTop);
    header.classList.toggle('fb-header--light', openPanelId == null && y < solidFrom);
  }

  /* ------------------------------------------------------------ megamenu */

  function closeMegamenu() {
    if (openPanelId == null) return;
    openPanelId = null;
    panels.forEach((panel) => {
      panel.hidden = true;
    });
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    header.classList.remove('fb-header--menu-open');
    updateSkin();
  }

  function openMegamenu(panelId) {
    if (!desktop.matches || openPanelId === panelId) return;
    openPanelId = panelId;
    panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });
    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.fbMegamenuTrigger === panelId));
    });
    header.classList.add('fb-header--menu-open');
    updateSkin();
  }

  triggers.forEach((trigger) => {
    const panelId = trigger.dataset.fbMegamenuTrigger;
    trigger.addEventListener('pointerenter', () => openMegamenu(panelId));
    trigger.addEventListener('focus', () => openMegamenu(panelId));
  });

  // Item de 1º nível SEM megamenu também fecha o painel aberto ao ser apontado/focado.
  header.querySelectorAll('.fb-header__nav-link:not([data-fb-megamenu-trigger])').forEach((link) => {
    link.addEventListener('pointerenter', closeMegamenu);
    link.addEventListener('focus', closeMegamenu);
  });

  header.addEventListener('pointerleave', closeMegamenu);
  header.addEventListener('focusout', (event) => {
    if (!header.contains(event.relatedTarget)) closeMegamenu();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMegamenu();
  });
  desktop.addEventListener('change', () => {
    closeMegamenu();
    measure();
    updateSkin();
  });

  /* ------------------------------------------------------------ listeners */

  if (!isTransparent || !anchor) return;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      updateSkin();
    });
  }

  measure();
  updateSkin();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      measure();
      updateSkin();
    },
    { passive: true }
  );
  window.addEventListener('load', () => {
    measure();
    updateSkin();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', flabomHeaderChrome, { once: true });
} else {
  flabomHeaderChrome();
}
